import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Power, Key, Shield, Activity, Server, AlertTriangle, 
  CheckCircle, XCircle, ExternalLink, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { getAgents, executeKillSwitch, reactivateAgent } from '../services/api';
import { toast } from 'sonner';

const KillSwitchPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [actionType, setActionType] = useState('agent_disabled');
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await getAgents();
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKillSwitch = async () => {
    if (!selectedAgent || !reason) {
      toast.error('Please select an agent and provide a reason');
      return;
    }

    setActionLoading(true);
    try {
      await executeKillSwitch({
        agent_id: selectedAgent.id,
        action_type: actionType,
        reason: reason
      });
      toast.success(`Kill switch action executed: ${actionType}`);
      setSelectedAgent(null);
      setReason('');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to execute kill switch');
    }
    setActionLoading(false);
  };

  const handleReactivate = async (agentId) => {
    try {
      await reactivateAgent(agentId);
      toast.success('Agent reactivated');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to reactivate agent');
    }
  };

  const filteredAgents = agents.filter((agent) => 
    agent.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeAgents = filteredAgents.filter(a => a.status === 'active');
  const containedAgents = filteredAgents.filter(a => a.status !== 'active');

  const getActionIcon = (type) => {
    switch (type) {
      case 'agent_disabled': return Power;
      case 'token_revoked': return Key;
      case 'approval_mode_enabled': return Shield;
      case 'tool_blocked': return Activity;
      case 'access_revoked': return Server;
      default: return AlertTriangle;
    }
  };

  const getActionLabel = (type) => {
    const labels = {
      'agent_disabled': 'Disable Agent',
      'token_revoked': 'Revoke All Tokens',
      'approval_mode_enabled': 'Enable Approval Mode',
      'tool_blocked': 'Block All Tools',
      'access_revoked': 'Revoke All Access',
    };
    return labels[type] || type;
  };

  return (
    <div data-testid="killswitch-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Power className="w-8 h-8 text-[#FF3B30]" />
          <h1 className="text-3xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Kill Switch Console
          </h1>
        </div>
        <p className="text-[#9CA3AF]">
          Immediately contain, disable, or revoke access for any agent or non-human identity.
        </p>
      </div>

      {/* Kill Switch Panel */}
      <div className="enterprise-card p-6 mb-8 border-l-4 border-[#FF3B30]">
        <h2 className="text-xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Execute Kill Switch
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Select Agent</Label>
              <Select 
                value={selectedAgent?.id || ''} 
                onValueChange={(id) => setSelectedAgent(agents.find(a => a.id === id))}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="select-agent-killswitch">
                  <SelectValue placeholder="Choose an agent..." />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-[300px]">
                  {activeAgents.map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      <div className="flex items-center gap-2">
                        <span>{agent.name}</span>
                        <span className={`text-xs ${
                          agent.risk_level === 'critical' ? 'text-[#FF3B30]' :
                          agent.risk_level === 'high' ? 'text-[#FF6961]' :
                          'text-[#9CA3AF]'
                        }`}>
                          ({agent.risk_level})
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Action Type</Label>
              <Select value={actionType} onValueChange={setActionType}>
                <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none" data-testid="select-action-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="agent_disabled">Disable Agent</SelectItem>
                  <SelectItem value="token_revoked">Revoke All Tokens</SelectItem>
                  <SelectItem value="approval_mode_enabled">Enable Approval Mode</SelectItem>
                  <SelectItem value="tool_blocked">Block All Tools</SelectItem>
                  <SelectItem value="access_revoked">Revoke All Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#9CA3AF]">Reason (Required)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this action..."
                className="bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
                data-testid="killswitch-reason"
              />
            </div>
          </div>
          <div className="flex flex-col justify-between">
            {selectedAgent && (
              <div className="enterprise-card p-4 mb-4">
                <h3 className="text-sm font-bold text-[#9CA3AF] mb-3">SELECTED AGENT</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Name</span>
                    <span className="text-[#F9FAFB] font-mono">{selectedAgent.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Type</span>
                    <span className="text-[#9CA3AF]">{selectedAgent.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Risk Level</span>
                    <span className={`
                      ${selectedAgent.risk_level === 'critical' ? 'text-[#FF3B30]' :
                        selectedAgent.risk_level === 'high' ? 'text-[#FF6961]' :
                        selectedAgent.risk_level === 'medium' ? 'text-[#FFCC00]' :
                        'text-[#34C759]'}
                    `}>
                      {selectedAgent.risk_level.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Credentials</span>
                    <span className="text-[#F9FAFB] font-mono">{selectedAgent.credentials?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#6B7280]">Tools</span>
                    <span className="text-[#F9FAFB] font-mono">{selectedAgent.tools?.length || 0}</span>
                  </div>
                </div>
              </div>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  className="kill-switch-btn w-full py-6 text-lg animate-pulse-glow"
                  disabled={!selectedAgent || !reason || actionLoading}
                  data-testid="execute-killswitch-btn"
                >
                  <Power className="w-5 h-5 mr-2" />
                  EXECUTE KILL SWITCH
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#121212] border-[#27272A]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-[#FF3B30] flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Confirm Kill Switch Action
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-[#9CA3AF]">
                    You are about to execute <span className="text-[#FF3B30] font-bold">{getActionLabel(actionType)}</span> on 
                    <span className="text-[#F9FAFB] font-mono"> {selectedAgent?.name}</span>.
                    <br /><br />
                    This action will be logged and may immediately impact production systems.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="secondary-btn">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="kill-switch-btn"
                    onClick={handleKillSwitch}
                    disabled={actionLoading}
                  >
                    Confirm Execution
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Contained Agents */}
      {containedAgents.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-[#F9FAFB] mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Contained Agents ({containedAgents.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {containedAgents.map((agent) => (
              <div key={agent.id} className="enterprise-card p-4 border-l-4 border-[#FFCC00]" data-testid={`contained-agent-${agent.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[#F9FAFB]">{agent.name}</span>
                  <span className={`px-2 py-0.5 text-xs font-mono ${
                    agent.status === 'disabled' ? 'text-[#FF3B30] bg-[#FF3B30]/10' :
                    agent.status === 'approval_required' ? 'text-[#FFCC00] bg-[#FFCC00]/10' :
                    'text-[#9CA3AF] bg-[#9CA3AF]/10'
                  }`}>
                    {agent.status}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <Link 
                    to={`/dashboard/agents/${agent.id}`}
                    className="text-[#0066FF] hover:text-[#3385FF] text-sm flex items-center gap-1"
                  >
                    View Details <ExternalLink className="w-3 h-3" />
                  </Link>
                  <Button
                    size="sm"
                    onClick={() => handleReactivate(agent.id)}
                    className="primary-btn text-xs"
                    data-testid={`reactivate-${agent.id}`}
                  >
                    Reactivate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Agents Quick Actions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#F9FAFB]" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Active Agents ({activeAgents.length})
          </h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]" />
            <Input
              placeholder="Search agents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#1A1A1A] border-[#27272A] text-[#F9FAFB] rounded-none"
              data-testid="search-active-agents"
            />
          </div>
        </div>
        {loading ? (
          <div className="enterprise-card p-8 text-center text-[#9CA3AF]">Loading agents...</div>
        ) : activeAgents.length === 0 ? (
          <div className="enterprise-card p-8 text-center">
            <CheckCircle className="w-12 h-12 text-[#34C759] mx-auto mb-4" />
            <p className="text-[#9CA3AF]">No active agents to display.</p>
          </div>
        ) : (
          <div className="enterprise-card overflow-hidden">
            <table className="data-table" data-testid="active-agents-table">
              <thead>
                <tr className="bg-[#1A1A1A]">
                  <th>Agent Name</th>
                  <th>Risk</th>
                  <th>Tools</th>
                  <th>Credentials</th>
                  <th>Environment</th>
                  <th>Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeAgents.map((agent) => (
                  <tr key={agent.id} data-testid={`active-agent-row-${agent.id}`}>
                    <td>
                      <Link 
                        to={`/dashboard/agents/${agent.id}`}
                        className="text-[#F9FAFB] hover:text-[#0066FF]"
                      >
                        {agent.name}
                      </Link>
                    </td>
                    <td>
                      <span className={`
                        ${agent.risk_level === 'critical' ? 'risk-critical' :
                          agent.risk_level === 'high' ? 'risk-high' :
                          agent.risk_level === 'medium' ? 'risk-medium' :
                          'risk-low'}
                      `}>
                        {agent.risk_level}
                      </span>
                    </td>
                    <td className="font-mono text-[#0066FF]">{agent.tools_count}</td>
                    <td className="font-mono text-[#FFCC00]">{agent.credentials?.length || 0}</td>
                    <td className="text-[#9CA3AF]">{agent.environment}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedAgent(agent);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-[#FF3B30] border-[#FF3B30]/50 hover:bg-[#FF3B30]/10"
                        data-testid={`quick-select-${agent.id}`}
                      >
                        <Power className="w-3 h-3 mr-1" />
                        Select
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KillSwitchPage;
